import { scrubText, scrubUrl } from './scrub';
import { buildEnglishByText, clickActionName } from './actionName';

// Map requests carry typed addresses and coordinates (Places, Geocoder, HERE routing) and map
// tiles reveal where a user is looking. Usage analytics needs none of them.
const DROPPED_RESOURCE_RE = /^https?:\/\/(?:[^/]+\.)?(?:googleapis\.com|gstatic\.com|hereapi\.com)\//i;
// Event types dropped when they happened before consent. Views cannot be dropped (and view_update
// events are derived from views after this hook).
const CONSENT_GATED_TYPES = ['action', 'error', 'long_task', 'resource'];

/* eslint-disable no-param-reassign */
function scrubView(view) {
  view.url = scrubUrl(view.url);
  if (view.referrer) view.referrer = scrubUrl(view.referrer);
  const lcp = view.performance?.lcp;
  if (lcp?.resource_url) lcp.resource_url = scrubUrl(lcp.resource_url);
}

function scrubError(error, context) {
  // An unhandled rejection with a plain value: repository.js rejects with the backend's response
  // body, whose message can name riders and drivers. Datadog would send its JSON as the message.
  if (error.source === 'source' && !(context?.error instanceof Error)) {
    const errorId = context?.error?.error?.errorId;
    error.message = `Uncaught non-Error value${Number.isInteger(errorId) ? ` (errorId ${errorId})` : ''}`;
    // Datadog only applies a change that keeps the field's type, so not undefined
    error.stack = '';
  } else {
    error.message = scrubText(error.message);
    if (error.stack) error.stack = scrubText(error.stack);
  }
  if (error.resource) error.resource.url = scrubUrl(error.resource.url);
}

// Long animation frames list the scripts that ran, with their URLs and invokers
function scrubLongTask(longTask) {
  (longTask.scripts || []).forEach((script) => {
    if (script.source_url) script.source_url = scrubUrl(script.source_url);
    if (script.invoker) script.invoker = scrubText(script.invoker);
  });
}
/* eslint-enable no-param-reassign */

/**
 * The `beforeSend` hook for Datadog RUM: keeps tokens, emails and typed data out of every event.
 * Returning false drops the event (Datadog ignores it for views, which cannot be dropped).
 * @param {object} i18n the app's VueI18n instance, for language-independent click names
 * @param {(date: number) => boolean} isBeforeConsent events from before consent are dropped
 * @returns {(event: object, context: object) => boolean}
 */
export default function createBeforeSend(i18n, isBeforeConsent = () => false) {
  const englishByLocale = new Map();
  const englishStrings = () => {
    const locale = i18n?.locale;
    if (!englishByLocale.has(locale)) englishByLocale.set(locale, buildEnglishByText(i18n?.messages, locale));
    return englishByLocale.get(locale);
  };

  return function beforeSend(event, context) {
    /* eslint-disable no-param-reassign */
    try {
      if (event.view) scrubView(event.view);
      if (CONSENT_GATED_TYPES.includes(event.type) && isBeforeConsent(event.date)) return false;

      switch (event.type) {
        case 'resource':
          if (DROPPED_RESOURCE_RE.test(event.resource.url)) return false;
          event.resource.url = scrubUrl(event.resource.url);
          break;
        case 'error':
          // console.error carries backend business-error text (repository.js logs every one)
          if (event.error.source === 'console') return false;
          scrubError(event.error, context);
          break;
        case 'long_task':
          scrubLongTask(event.long_task);
          break;
        case 'action':
          if (event.action.type === 'click' && event.action.target) {
            const name = clickActionName(
              context?.events?.[0]?.target,
              event.action.target.name,
              englishStrings(),
              event._dd?.action?.name_source, // eslint-disable-line no-underscore-dangle
            );
            if (name) event.action.target.name = name;
          }
          break;
        default:
      }
      return true;
    } catch (error) {
      // A scrubbing failure must not send what it failed to scrub
      return false;
    }
    /* eslint-enable no-param-reassign */
  };
}
